'use client'

import { useState, useEffect } from 'react'
import AvaIconOrb from './AvaIconOrb'

// Constellation — features connect into a neural web around AVA
export function HeroConstellation({ speed = 1 }) {
  const [t, setT] = useState(0)
  useEffect(() => {
    let raf
    const start = performance.now()
    const loop = (now) => {
      setT(((now - start) / 1000) * speed)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [speed])

  const nodes = [
    { label: 'Calendar', x: 18, y: 22, icon: '◷' },
    { label: 'Tasks',    x: 82, y: 18, icon: '✓' },
    { label: 'Notes',    x: 12, y: 55, icon: '≡' },
    { label: 'Email',    x: 88, y: 52, icon: '✉' },
    { label: 'Meetings', x: 24, y: 85, icon: '●' },
    { label: 'Research', x: 78, y: 85, icon: '◈' },
  ]

  return (
    <div style={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', maxWidth: 540, margin: '0 auto' }}>
      <div style={{
        position: 'absolute', inset: '20%', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(50,105,248,0.3), transparent 70%)',
        filter: 'blur(40px)',
      }} />

      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 100 100" preserveAspectRatio="none">
        {nodes.map((n, i) => {
          const pulse = 0.3 + Math.abs(Math.sin(t * 2 + i)) * 0.6
          return (
            <line key={i} x1={n.x} y1={n.y} x2="50" y2="50" stroke="#3269F8" strokeWidth="0.15" opacity={pulse} strokeDasharray="1 1" />
          )
        })}
        {nodes.map((n, i) => {
          const p = ((t * 0.5 + i * 0.17) % 1)
          const x = n.x + (50 - n.x) * p
          const y = n.y + (50 - n.y) * p
          return <circle key={'p' + i} cx={x} cy={y} r="0.7" fill="#3269F8" opacity={Math.sin(p * Math.PI)} />
        })}
      </svg>

      {nodes.map((n, i) => (
        <div key={n.label} style={{
          position: 'absolute', left: `${n.x}%`, top: `${n.y}%`,
          transform: 'translate(-50%, -50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: '#fff', border: '1px solid #E7E3D9',
            boxShadow: '0 8px 20px rgba(20,14,50,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, color: '#3269F8',
            animation: `avaFloatNode ${3 + i * 0.3}s ${i * 0.2}s ease-in-out infinite`,
          }}>{n.icon}</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#4A4945' }}>{n.label}</div>
        </div>
      ))}

      <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
        <AvaIconOrb size={140} pulse />
      </div>
    </div>
  )
}

// Morning — AVA narrates a schedule
export function HeroMorning({ speed = 1 }) {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), 800 / speed)
    return () => clearInterval(t)
  }, [speed])

  const script = [
    { kind: 'ava', text: "Good morning. Here's your day:" },
    { kind: 'ev', time: '9:00', title: 'Team standup', tone: '#3269F8' },
    { kind: 'ev', time: '11:30', title: 'Design review · Figma', tone: '#6FA0FF' },
    { kind: 'ev', time: '14:00', title: 'Lunch w/ Priya', tone: '#FDCB6E' },
    { kind: 'ev', time: '16:30', title: 'Draft Q2 proposal', tone: '#1E47C7' },
    { kind: 'ava', text: '3 hours of focus protected.' },
  ]
  const shown = Math.min(Math.floor(tick / 2) + 1, script.length)

  return (
    <div style={{
      position: 'relative', width: '100%', aspectRatio: '1 / 1', maxWidth: 540, margin: '0 auto',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px',
    }}>
      <div style={{
        position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)',
        width: 300, height: 300, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(50,105,248,0.25), transparent 70%)',
        filter: 'blur(40px)',
      }} />

      <div style={{ position: 'relative', width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <AvaIconOrb size={52} pulse />
          <div>
            <div style={{ fontSize: 11, color: '#3269F8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>AVA · 7:42 AM</div>
            <div style={{ fontSize: 12, color: '#6B6A66', marginTop: 2 }}>Morning brief</div>
          </div>
        </div>

        {script.slice(0, shown).map((s, i) => {
          if (s.kind === 'ava') return (
            <div key={i} style={{
              alignSelf: 'flex-start',
              background: '#fff', border: '1px solid #EFEBE3',
              borderRadius: '16px 16px 16px 4px',
              padding: '10px 14px',
              fontSize: 14, color: '#0E0E0C', fontWeight: 500,
              boxShadow: '0 4px 12px rgba(20,14,50,0.04)',
              animation: 'avaSlideIn 400ms cubic-bezier(0.2,0.8,0.2,1) both',
              maxWidth: '85%',
            }}>{s.text}</div>
          )
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 14px',
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(12px)',
              border: '1px solid #EFEBE3', borderLeft: `3px solid ${s.tone}`,
              borderRadius: 14, boxShadow: '0 4px 12px rgba(20,14,50,0.04)',
              animation: 'avaSlideIn 500ms cubic-bezier(0.2,0.8,0.2,1) both',
            }}>
              <div style={{ fontSize: 11, color: '#6B6A66', fontWeight: 600, width: 44, fontVariantNumeric: 'tabular-nums' }}>{s.time}</div>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 500, color: '#0E0E0C' }}>{s.title}</div>
              <div style={{ width: 8, height: 8, borderRadius: 4, background: s.tone }} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Command — a live command is typed & executed
export function HeroCommand({ speed = 1 }) {
  const prompts = [
    { text: 'Reschedule my 3pm to tomorrow morning', out: [
      { icon: '◷', label: 'Moved to Thu, 9:00 AM', t: 'Calendar' },
      { icon: '✉', label: 'Notified 3 attendees', t: 'Email' },
      { icon: '✓', label: 'Added prep task for tonight', t: 'Task' },
    ]},
    { text: 'Summarize the client call I just had', out: [
      { icon: '▤', label: '3 key decisions, 5 action items', t: 'Notes' },
      { icon: '✓', label: 'Tasks assigned to Alex, Nora', t: 'Task' },
      { icon: '✉', label: 'Drafted follow-up email', t: 'Email' },
    ]},
    { text: "What's on my plate today?", out: [
      { icon: '◷', label: '5 meetings · 3 focus blocks', t: 'Brief' },
      { icon: '✓', label: 'Top priority: Q2 proposal', t: 'Focus' },
      { icon: '⚠', label: '2pm/2:30 conflict — fix?', t: 'Alert' },
    ]},
  ]
  const [promptIdx, setPromptIdx] = useState(0)
  const [typed, setTyped] = useState('')
  const [stage, setStage] = useState(0)

  useEffect(() => {
    const prompt = prompts[promptIdx].text
    setTyped(''); setStage(0)
    let i = 0
    const timeouts = []
    const typer = setInterval(() => {
      i++
      setTyped(prompt.slice(0, i))
      if (i >= prompt.length) {
        clearInterval(typer)
        timeouts.push(setTimeout(() => setStage(1), 400 / speed))
        timeouts.push(setTimeout(() => setStage(2), 1400 / speed))
        timeouts.push(setTimeout(() => setPromptIdx(p => (p + 1) % prompts.length), 5000 / speed))
      }
    }, 45 / speed)
    return () => {
      clearInterval(typer)
      timeouts.forEach(t => clearTimeout(t))
    }
  }, [promptIdx, speed])

  const current = prompts[promptIdx]

  return (
    <div style={{
      position: 'relative', width: '100%', aspectRatio: '1 / 1', maxWidth: 540, margin: '0 auto',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 22,
    }}>
      <div style={{
        position: 'absolute', inset: '15%', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(50,105,248,0.3), transparent 70%)',
        filter: 'blur(40px)',
      }} />

      <div style={{ position: 'relative' }}>
        <AvaIconOrb size={88} pulse={stage === 1} />
      </div>

      <div style={{
        position: 'relative', width: '90%',
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.9)',
        boxShadow: '0 20px 50px rgba(20,14,50,0.12)',
        borderRadius: 20, padding: '16px 18px',
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
          textTransform: 'uppercase', color: '#3269F8', marginBottom: 6,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: 3, background: '#EF4444',
            animation: 'avaPulse 1.2s ease-in-out infinite',
          }} />
          {stage === 0 ? 'Listening' : stage === 1 ? 'Thinking' : 'Done'}
        </div>
        <div style={{ fontSize: 17, fontWeight: 500, color: '#0E0E0C', minHeight: 46, lineHeight: 1.3 }}>
          &ldquo;{typed}&rdquo;
          {stage === 0 && (
            <span style={{
              display: 'inline-block', width: 2, height: 18, background: '#3269F8',
              marginLeft: 2, verticalAlign: 'middle',
              animation: 'avaBlink 0.9s step-end infinite',
            }} />
          )}
        </div>
      </div>

      <div style={{ width: '90%', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {current.out.map((o, i) => (
          <div key={`${promptIdx}-${i}`} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px',
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(8px)',
            border: '1px solid #EFEBE3', borderRadius: 12,
            opacity: stage === 2 ? 1 : 0,
            transform: `translateY(${stage === 2 ? 0 : 8}px)`,
            transition: `all 400ms cubic-bezier(0.2,0.8,0.2,1) ${i * 100}ms`,
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: 8,
              background: 'linear-gradient(135deg, #3269F8, #6FA0FF)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 13, fontWeight: 700,
            }}>{o.icon}</div>
            <div style={{ flex: 1, fontSize: 13, fontWeight: 500, color: '#0E0E0C' }}>{o.label}</div>
            <div style={{
              fontSize: 9, fontWeight: 700, color: '#3269F8',
              background: 'rgba(50,105,248,0.1)',
              padding: '3px 6px', borderRadius: 5,
              textTransform: 'uppercase', letterSpacing: 0.4,
            }}>{o.t}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Orbit — voice rings around AVA icon, sparks orbit
export function HeroOrbit({ speed = 1 }) {
  const [t, setT] = useState(0)
  useEffect(() => {
    let raf
    const start = performance.now()
    const loop = (now) => {
      setT(((now - start) / 1000) * speed)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [speed])

  const bars = 64

  return (
    <div style={{
      position: 'relative', width: '100%', aspectRatio: '1 / 1', maxWidth: 540, margin: '0 auto',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        position: 'absolute', inset: '15%', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(50,105,248,0.4), transparent 70%)',
        filter: 'blur(40px)',
      }} />

      <svg viewBox="-200 -200 400 400" style={{ width: '85%', height: '85%', position: 'absolute' }}>
        {Array.from({ length: bars }).map((_, i) => {
          const angle = (i / bars) * Math.PI * 2
          const wave = Math.sin(i * 0.35 + t * 3) * 0.5 + Math.sin(i * 0.18 - t * 1.7) * 0.5
          const len = 10 + Math.abs(wave) * 26 + 4
          const r1 = 130
          const r2 = r1 + len
          return (
            <line key={i}
              x1={Math.cos(angle) * r1} y1={Math.sin(angle) * r1}
              x2={Math.cos(angle) * r2} y2={Math.sin(angle) * r2}
              stroke="#3269F8" strokeWidth="2.4" strokeLinecap="round"
              opacity={0.35 + Math.abs(wave) * 0.5}
            />
          )
        })}
      </svg>

      {[0, 1, 2, 3, 4].map(i => {
        const ang = t * 0.5 + (i * Math.PI * 2) / 5
        const r = 175
        return (
          <div key={i} style={{
            position: 'absolute', left: '50%', top: '50%',
            transform: `translate(calc(-50% + ${Math.cos(ang) * r}px), calc(-50% + ${Math.sin(ang) * r}px))`,
            width: 10, height: 10, borderRadius: 5,
            background: '#fff',
            boxShadow: '0 0 16px 4px rgba(50,105,248,0.6), 0 0 4px rgba(50,105,248,1)',
          }} />
        )
      })}

      <div style={{ position: 'relative', zIndex: 2 }}>
        <AvaIconOrb size={200} pulse />
      </div>
    </div>
  )
}
